import { toolbarFamily } from "../tools-config";
import {
  ChevronLeft, ChevronRight, AlignLeft, AlignCenter, AlignRight, AlignJustify,
  Bold, Italic, Underline, Copy, Trash2, Lock, Wand2, Image as ImageIcon,
  Crop, RefreshCw, Layers, Sparkles, Download, ShieldCheck, FileText,
} from "lucide-react";

/**
 * Dynamic contextual toolbar — switches based on selected tool family.
 */
export default function ContextualToolbar({ selectedTool }) {
  const family = toolbarFamily(selectedTool);
  return (
    <div
      data-testid={`ctx-toolbar-${family}`}
      className="flex items-center gap-1 px-3 h-11 border-b border-[var(--pl-border)] bg-[var(--pl-surface)]/60 overflow-x-auto pl-noscroll"
    >
      {family === "selection" && <SelectionToolbar />}
      {family === "text" && <TextToolbar />}
      {family === "textframe" && <TextFrameToolbar />}
      {family === "image" && <ImageToolbar />}
      {family === "shape" && <ShapeToolbar />}
      {family === "page" && <PageToolbar />}
      {family === "ai" && <AIToolbar />}
      {family === "export" && <ExportToolbar />}
    </div>
  );
}

const Num = ({ label, defaultValue, w = 64 }) => (
  <label className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-[var(--pl-text-muted)]">
    {label}
    <input
      defaultValue={defaultValue}
      style={{ width: w, background: "color-mix(in srgb, var(--pl-surface) 70%, transparent)", color: "var(--pl-text)" }}
      className="h-7 rounded-md border border-[var(--pl-border)] px-2 text-xs"
    />
  </label>
);

const Sel = ({ defaultValue, options, w = 120 }) => (
  <select
    defaultValue={defaultValue}
    style={{ width: w, background: "color-mix(in srgb, var(--pl-surface) 70%, transparent)", color: "var(--pl-text)" }}
    className="h-7 rounded-md border border-[var(--pl-border)] px-2 text-xs"
  >
    {options.map((o) => <option key={o}>{o}</option>)}
  </select>
);

const IBtn = ({ label, icon: I, testId }) => (
  <button
    data-testid={testId}
    title={label}
    aria-label={label}
    className="h-7 w-7 grid place-items-center rounded-md hover:bg-white/5 text-[var(--pl-text-muted)] hover:text-[var(--pl-aqua)]"
  >
    <I className="h-3.5 w-3.5" />
  </button>
);

const Divider = () => <span className="h-5 w-px bg-[var(--pl-border)] mx-1" />;

function SelectionToolbar() {
  return (
    <>
      <Num label="X" defaultValue="120" />
      <Num label="Y" defaultValue="186" />
      <Num label="W" defaultValue="320" />
      <Num label="H" defaultValue="240" />
      <Num label="°" defaultValue="0" w={48} />
      <Num label="%" defaultValue="100" w={48} />
      <Divider />
      <IBtn label="Align Left" icon={AlignLeft} testId="ctx-align-left" />
      <IBtn label="Align Center" icon={AlignCenter} testId="ctx-align-center" />
      <IBtn label="Align Right" icon={AlignRight} testId="ctx-align-right" />
      <Divider />
      <IBtn label="Bring Forward" icon={ChevronRight} />
      <IBtn label="Send Backward" icon={ChevronLeft} />
      <IBtn label="Lock" icon={Lock} />
      <IBtn label="Duplicate" icon={Copy} />
      <IBtn label="Delete" icon={Trash2} />
    </>
  );
}

function TextToolbar() {
  return (
    <>
      <Sel defaultValue="Outfit" options={["Outfit", "Playfair Display", "Manrope", "IBM Plex Serif"]} w={140} />
      <Sel defaultValue="Regular" options={["Light", "Regular", "Medium", "Semibold", "Bold"]} w={104} />
      <Num label="Size" defaultValue="12" w={56} />
      <Num label="Lh" defaultValue="16" w={56} />
      <Num label="Tr" defaultValue="0" w={48} />
      <Divider />
      <Sel defaultValue="Body Text" options={["Header Part", "Header Chapter", "Header 1", "Body Text", "Body Text Mini", "Bullet Point"]} w={140} />
      <IBtn label="Bold" icon={Bold} />
      <IBtn label="Italic" icon={Italic} />
      <IBtn label="Underline" icon={Underline} />
      <Divider />
      <IBtn label="Align Left" icon={AlignLeft} />
      <IBtn label="Align Center" icon={AlignCenter} />
      <IBtn label="Align Right" icon={AlignRight} />
      <IBtn label="Justify" icon={AlignJustify} />
      <Divider />
      <button data-testid="ctx-auto-style" className="h-7 px-2.5 rounded-md text-[11px] inline-flex items-center gap-1.5 text-[#061414] pl-btn-aqua">
        <Sparkles className="h-3 w-3" /> Auto Style with AI
      </button>
    </>
  );
}

function TextFrameToolbar() {
  return (
    <>
      <Num label="Cols" defaultValue="1" w={48} />
      <Num label="Gut" defaultValue="0.17" w={64} />
      <Num label="Inset" defaultValue="6" w={56} />
      <Sel defaultValue="Top" options={["Top", "Center", "Bottom", "Justify"]} w={88} />
      <Divider />
      <IBtn label="Link Frame" icon={Layers} />
      <button className="h-7 px-2.5 rounded-md text-[11px] border border-[var(--pl-border)] text-[var(--pl-text)] hover:border-[var(--pl-aqua)]/40">
        Auto-flow text
      </button>
      <button className="h-7 px-2.5 rounded-md text-[11px] border border-rose-500/40 text-rose-300">
        Show overset
      </button>
      <button data-testid="ctx-fix-overset" className="h-7 px-2.5 rounded-md text-[11px] pl-btn-aqua text-[#061414] inline-flex items-center gap-1.5">
        <Wand2 className="h-3 w-3" /> Fix overset with AI
      </button>
    </>
  );
}

function ImageToolbar() {
  return (
    <>
      <button data-testid="ctx-replace-image" className="h-7 px-2.5 rounded-md text-[11px] border border-[var(--pl-border)] text-[var(--pl-text)] hover:border-[var(--pl-aqua)]/40 inline-flex items-center gap-1.5">
        <ImageIcon className="h-3 w-3" /> Replace
      </button>
      <IBtn label="Crop" icon={Crop} />
      <button className="h-7 px-2.5 rounded-md text-[11px] border border-[var(--pl-border)] text-[var(--pl-text)] hover:border-[var(--pl-aqua)]/40">
        Fit Proportionally
      </button>
      <button className="h-7 px-2.5 rounded-md text-[11px] border border-[var(--pl-border)] text-[var(--pl-text)] hover:border-[var(--pl-aqua)]/40">
        Fill Frame
      </button>
      <Divider />
      <Sel defaultValue="Bounding Box" options={["No Wrap", "Bounding Box", "Object Shape", "Jump Object", "Left", "Right", "Both"]} w={140} />
      <Num label="Off" defaultValue="6" w={56} />
      <Num label="Op" defaultValue="100" w={48} />
      <Num label="Radius" defaultValue="0" w={56} />
      <Divider />
      <button data-testid="ctx-ai-image" className="h-7 px-2.5 rounded-md text-[11px] pl-btn-aqua text-[#061414] inline-flex items-center gap-1.5">
        <Wand2 className="h-3 w-3" /> AI matching image
      </button>
      <button className="h-7 px-2.5 rounded-md text-[11px] border border-[var(--pl-border)] text-[var(--pl-text)] hover:border-[var(--pl-aqua)]/40">
        Remove background
      </button>
    </>
  );
}

function ShapeToolbar() {
  return (
    <>
      <Sel defaultValue="Rectangle" options={["Rectangle", "Ellipse", "Line", "Polygon"]} w={120} />
      <Num label="Fill" defaultValue="#27E6D1" w={88} />
      <Num label="Stroke" defaultValue="#0BAF83" w={88} />
      <Num label="Stroke W" defaultValue="1" w={56} />
      <Num label="Radius" defaultValue="8" w={56} />
      <Num label="Opacity" defaultValue="100" w={56} />
      <Divider />
      <button className="h-7 px-2.5 rounded-md text-[11px] border border-[var(--pl-border)] text-[var(--pl-text)]">Convert to Frame</button>
    </>
  );
}

function PageToolbar() {
  return (
    <>
      <Sel defaultValue="6 × 9 in" options={["6 × 9 in", "5.5 × 8.5 in", "8.5 × 11 in", "A4"]} w={130} />
      <Sel defaultValue="Portrait" options={["Portrait", "Landscape"]} w={110} />
      <button className="h-7 px-2.5 rounded-md text-[11px] border border-[var(--pl-border)] text-[var(--pl-text)]">Facing pages</button>
      <Num label="M" defaultValue="0.75" w={64} />
      <Num label="Bleed" defaultValue="0.125" w={64} />
      <Num label="Slug" defaultValue="0.125" w={64} />
      <Divider />
      <button data-testid="ctx-add-page" className="h-7 px-2.5 rounded-md text-[11px] pl-btn-aqua text-[#061414]">+ Add Page</button>
      <button className="h-7 px-2.5 rounded-md text-[11px] border border-[var(--pl-border)] text-[var(--pl-text)]">Duplicate page</button>
      <Sel defaultValue="A Body Page" options={["A Body Page", "B Chapter Opener", "C Workbook Page"]} w={150} />
      <Sel defaultValue="This page" options={["This page", "Selected pages", "Chapter", "Whole book"]} w={130} />
    </>
  );
}

function AIToolbar() {
  return (
    <>
      <button data-testid="ctx-ai-image-btn" className="h-7 px-2.5 rounded-md text-[11px] pl-btn-aqua text-[#061414] inline-flex items-center gap-1.5">
        <ImageIcon className="h-3 w-3" /> Image
      </button>
      <button className="h-7 px-2.5 rounded-md text-[11px] border border-[var(--pl-border)] text-[var(--pl-text)]">Diagram</button>
      <button className="h-7 px-2.5 rounded-md text-[11px] border border-[var(--pl-border)] text-[var(--pl-text)]">Rewrite</button>
      <button className="h-7 px-2.5 rounded-md text-[11px] border border-[var(--pl-border)] text-[var(--pl-text)]">Chapter opener</button>
      <button className="h-7 px-2.5 rounded-md text-[11px] border border-[var(--pl-border)] text-[var(--pl-text)]">Workbook exercise</button>
      <button className="h-7 px-2.5 rounded-md text-[11px] border border-[var(--pl-border)] text-[var(--pl-text)]">Pull quote</button>
      <button className="h-7 px-2.5 rounded-md text-[11px] border border-[var(--pl-border)] text-[var(--pl-text)]">Cover concept</button>
      <Divider />
      <button className="h-7 px-2.5 rounded-md text-[11px] border border-[var(--pl-border)] text-[var(--pl-text)]">Fix layout</button>
      <button className="h-7 px-2.5 rounded-md text-[11px] border border-[var(--pl-border)] text-[var(--pl-text)]">Balance spread</button>
      <button className="h-7 px-2.5 rounded-md text-[11px] border border-[var(--pl-border)] text-[var(--pl-text)]">Fix typography</button>
      <button className="h-7 px-2.5 rounded-md text-[11px] border border-[var(--pl-border)] text-[var(--pl-text)]">Readability</button>
      <button data-testid="ctx-kdp-check" className="h-7 px-2.5 rounded-md text-[11px] inline-flex items-center gap-1 border border-[var(--pl-lime)]/40 text-[var(--pl-lime)]">
        <ShieldCheck className="h-3 w-3" /> KDP-ready
      </button>
    </>
  );
}

function ExportToolbar() {
  return (
    <>
      <button data-testid="ctx-preflight" className="h-7 px-2.5 rounded-md text-[11px] border border-[var(--pl-border)] text-[var(--pl-text)] inline-flex items-center gap-1.5">
        <ShieldCheck className="h-3 w-3" /> Preflight
      </button>
      <Divider />
      <button data-testid="ctx-export-pdf" className="h-7 px-2.5 rounded-md text-[11px] pl-btn-aqua text-[#061414] inline-flex items-center gap-1.5">
        <Download className="h-3 w-3" /> Export PDF
      </button>
      <button className="h-7 px-2.5 rounded-md text-[11px] border border-[var(--pl-border)] text-[var(--pl-text)]">KDP Interior</button>
      <button className="h-7 px-2.5 rounded-md text-[11px] border border-[var(--pl-border)] text-[var(--pl-text)]">EPUB</button>
      <button className="h-7 px-2.5 rounded-md text-[11px] border border-[var(--pl-border)] text-[var(--pl-text)]">DOCX</button>
      <button className="h-7 px-2.5 rounded-md text-[11px] border border-[var(--pl-border)] text-[var(--pl-text)]">Cover PDF</button>
      <button className="h-7 px-2.5 rounded-md text-[11px] border border-[var(--pl-border)] text-[var(--pl-text)]">Print package</button>
      <Sel defaultValue="All pages" options={["All pages", "Selected pages", "Chapter", "Range…"]} w={130} />
    </>
  );
}
